import React from 'react';
import { X, RotateCcw } from 'lucide-react';
import type { DashboardColorSettings } from '../contexts/DashboardSettingsContext';
import { useDashboardSettings } from '../contexts/DashboardSettingsContext';
import { ColorPicker } from './ColorPicker';

interface DashboardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  dashboardName: string;
}

export const DashboardSettingsModal: React.FC<DashboardSettingsModalProps> = ({
  isOpen,
  onClose,
  dashboardId,
  dashboardName
}) => {
  const { getCurrentSettings, updateSettings, resetSettings } = useDashboardSettings();

  const currentSettings = getCurrentSettings(dashboardId);

  if (!isOpen) return null;

  const handleColorChange = (colorKey: keyof DashboardColorSettings, newColor: string) => {
    updateSettings(dashboardId, { [colorKey]: newColor });
  };

  const handleBooleanChange = (key: string, value: boolean) => {
    updateSettings(dashboardId, { [key]: value });
  };

  const handleReset = () => {
    if (window.confirm(`Reset all settings for "${dashboardName}" dashboard to defaults?`)) {
      resetSettings(dashboardId);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Dashboard Settings
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Customize "{dashboardName}" dashboard appearance
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>


        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          <div className="space-y-6">
            {/* Color Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPicker
                label="Background Color"
                value={currentSettings.backgroundColor}
                onChange={(color) => handleColorChange('backgroundColor', color)}
              />
              
              <ColorPicker
                label="Widget Background"
                value={currentSettings.widgetBackground}
                onChange={(color) => handleColorChange('widgetBackground', color)}
              />
              
              <ColorPicker
                label="Widget Border"
                value={currentSettings.widgetBorder}
                onChange={(color) => handleColorChange('widgetBorder', color)}
              />
              
            </div>

            {/* Spacing Settings */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Widget Spacing
              </label>
              <div className="space-y-3">
                <input
                  type="range"
                  min="8"
                  max="32"
                  step="4"
                  value={currentSettings.widgetSpacing}
                  onChange={(e) => handleColorChange('widgetSpacing' as any, e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Tight (8px)</span>
                  <span className="font-medium">{currentSettings.widgetSpacing}px</span>
                  <span>Loose (32px)</span>
                </div>
              </div>
            </div>

            {/* Gridlines Settings */}
            <div className="mt-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentSettings.showGridlines}
                  onChange={(e) => handleBooleanChange('showGridlines', e.target.checked)}
                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Show Gridlines
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {/* Reset */}
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};